import styled, { css } from 'styled-components';
import { Typography } from '../../../global/theme';
import { mobile_xl } from '../../../global/mediaQueryMixin';
import { AlignCenter, FlexBetween, FlexCenter, HideScrollbar, LineClamp } from '../../../global/commonStyle';

interface Props {
  isMilestoneEmpty?: boolean;
  isProjectActivityCard?: boolean;
}

const LeftSection = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  padding-inline: var(--space-24);
  ${HideScrollbar}
  ${mobile_xl(css`
    padding-inline: var(--space-20);
  `)}
`;

const Group = styled.div<Props>`
  display: flex;
  max-width: 700px;
  width: 100%;
  margin: var(--space-0) auto;
  flex-direction: column;
  gap: var(--space-20);
  padding-block: var(--space-20) var(--space-24);
  height: max-content;
  ${mobile_xl(css`
    padding-block: var(--space-8) var(--space-96);
    gap: var(--space-16);
  `)}
`;
const PodcastDiv = styled.div`
  max-width: 700px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
`;
const Top = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  ${mobile_xl(css`
    display: none;
  `)}
`;

const Heading = styled.h1`
  ${Typography.heading_md}
  margin: var(--space-0);
  color: var(--text-primary);
  ${mobile_xl(css`
    display: none;
  `)}
`;

const Profiles = styled.div`
  ${AlignCenter}
  gap: var(--space-4);
  flex-wrap: wrap;
  .hide-button {
    display: none;
  }
  &:hover {
    cursor: pointer;
    .hide-button {
      display: block;
    }
  }

  ${mobile_xl(css`
    gap: var(--space-8);
    .hide-button {
      display: block;
    }
  `)}
`;
const ProfileBorder = styled.div`
  width: 34px;
  height: 34px;
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-full);
  ${FlexCenter}
`;
const ProfileGroup = styled.div`
  ${FlexCenter}
  gap: var(--space-2);
  height: 34px;
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-full);
  padding-inline: var(--space-2);
`;
const TaskDiv = styled.div`
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-12);
  box-shadow: var(--shadow-card);
  background-color: var(--background-primary);
  padding: var(--space-16);
  display: flex;
  gap: var(--space-20);
  ${mobile_xl(css`
    flex-direction: column;
  `)}
`;
const Task = styled.div`
  display: flex;
  gap: var(--space-16);
  max-width: 209px;
  width: 100%;
  border-right: 0.5px solid var(--border-primary);
  :last-child {
    border-right: none;
  }
  ${mobile_xl(css`
    border: none;
  `)}
`;
const Icon = styled.div`
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-6);
  background-color: var(--background-tertiary);
  width: 46px;
  height: 46px;
  ${FlexCenter}
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  p {
    ${Typography.body_md_medium}
    margin: var(--space-0);
    color: var(--text-secondary);
  }
  h2 {
    ${Typography.heading_lg}
    margin: var(--space-0);
    color: var(--text-primary);
  }
  ${mobile_xl(css`
    gap: var(--space-0);
  `)}
`;

const Header = styled.div`
  ${FlexBetween}
  span {
    ${Typography.heading_sm}
    margin: var(--space-0);
    color: var(--text-primary);
  }
`;

const MilestoneBox = styled.div<Props>`
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-12);
  box-shadow: var(--shadow-card);
  background-color: var(--background-primary);
  padding: var(--space-16) var(--space-16)
    ${({ isMilestoneEmpty }) => (isMilestoneEmpty ? 'var(--space-32)' : 'var(--space-6)')};
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  .milestone-empty {
    margin-inline: auto;
  }
  p {
    ${Typography.heading_sm}
    margin: var(--space-0);
    color: var(--text-primary);
  }
  ${mobile_xl(css`
    margin: var(--space-0);
  `)}
`;

const Milestone = styled.div``;

const Div = styled.div`
  ${FlexBetween}
  gap: var(--space-10);
  padding-block: var(--space-10);
  border-bottom: 0.5px solid var(--border-primary);
  :last-child {
    border-bottom: none;
  }
  ${mobile_xl(css`
    gap: var(--space-16);
  `)}
`;
const LeftDiv = styled.div`
  ${AlignCenter}
  gap: var(--space-10);
  .header-icon {
    fill: var(--background-primary);
    stroke: var(--text-secondary);
    path {
      stroke: none;
    }
  }
  .milestone-icon {
    fill: var(--background-primary);
    path.border {
      stroke: var(--text-secondary);
    }
  }
  p {
    ${Typography.body_md_medium}
    margin: var(--space-0);
    color: var(--text-primary);
    span {
      padding-left: var(--space-6);
    }
  }
  ${mobile_xl(css`
    align-items: flex-start;
  `)}
`;
const SubDiv = styled.div`
  display: flex;
  gap: var(--space-6);
  ${mobile_xl(css`
    flex-wrap: wrap;
    max-width: 231px;
    width: 100%;
  `)}
`;
const RightDiv = styled.div`
  ul {
    display: flex;
    flex-wrap: nowrap;
    list-style-type: none;
    padding: var(--space-0);
    margin: var(--space-0);
    justify-content: flex-end;
    height: 24px;
  }
  li {
    margin-left: -9px;
    width: 24px;
  }
`;
const AvatarImage = styled.div`
  ${AlignCenter}
  border-radius: var(--border-radius-full);

  > div > img {
    max-width: 100%;
  }
`;

const GraphBox = styled.div`
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-12);
  box-shadow: var(--shadow-card);
  background-color: var(--background-primary);
  padding: var(--space-16);
  display: flex;
  gap: var(--space-12);
  flex-direction: column;
`;

const Dropdowns = styled.div`
  position: relative;
  .dropdown-icon {
    transform: rotate(180deg);
  }
`;

const LoadingImage = styled.div`
  width: 100%;
  height: 400px;
  position: relative;
`;
const ResourcesSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--space-6);
  ${mobile_xl(css`
    flex-direction: column;
  `)}
`;
const ResourceTitle = styled.div`
  ${Typography.body_md_medium};
  color: var(--text-primary);
  padding-block: var(--space-3);
`;

const AddedLinkBox = styled.div`
  background-color: var(--background-primary);
  border-radius: var(--border-radius-8);
  padding: var(--space-3) var(--space-4) var(--space-3) var(--space-8);
  ${AlignCenter}
  gap: var(--space-4);
  box-shadow: var(--border);
  cursor: pointer;
  &:hover {
    background-color: var(--neutral-secondary);
  }
  .more-icon {
    &:hover {
      background-color: var(--background-muted);
    }
  }
  .active-more-icon {
    background-color: var(--background-muted);
    border-radius: var(--border-radius-4);
    svg path {
      fill: var(--text-primary);
    }
  }
`;

const AddedLinkBoxLeft = styled.div`
  ${AlignCenter}
  gap: var(--space-6);
`;

const LinkIcon = styled.div`
  display: flex;
`;

const Text = styled.p`
  margin: var(--space-0);
  ${Typography.body_md_medium};
  color: var(--text-primary);
`;

const ResourcesFileSection = styled.div`
  ${AlignCenter}
  gap: var(--space-6);
  flex-wrap: wrap;
  .resource-item {
    background-color: var(--neutral-secondary);
    border-radius: var(--border-radius-8);
    svg path {
      fill: var(--text-primary);
    }
  }
`;

const WrapperDiv = styled.div`
  height: calc(100dvh - 47px);
  display: flex;
  ${mobile_xl(css`
    height: unset;
  `)}
`;

const CardMainDiv = styled.div`
  display: flex;
  padding: var(--space-16);
  flex-direction: column;
  gap: var(--space-12);
  border-radius: var(--border-radius-12);
  border: 0.5px solid var(--border-primary);
  background: var(--background-primary);
  box-shadow: var(--shadow-card);
`;

const Title = styled.div`
  ${Typography.heading_sm};
  color: var(--text-primary);
`;

const ContentDiv = styled.div`
  ${Typography.body_md_medium};
  color: var(--text-primary);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-10);
`;

const TopSection = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  align-items: center;
  gap: var(--space-16);
  width: 100%;
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-14);
  width: 100%;
`;

const TextDiv = styled.p`
  ${Typography.body_sm_medium};
  color: var(--text-secondary);
`;

const RowDiv = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  align-items: center;
  gap: var(--space-16);
  width: 100%;
`;

const ChatContent = styled.div`
  ${AlignCenter}
  gap: var(--space-6);
`;

const ProgressSection = styled.div`
  ${AlignCenter}
  gap: var(--space-12);
  width: 100%;
`;

const StatusText = styled.p`
  ${Typography.body_md};
  color: var(--text-primary);
  margin: var(--space-0);
`;

const UserName = styled.p`
  ${LineClamp(1)}
  margin: var(--space-0);
  ${Typography.body_md};
  color: var(--text-primary);
`;

const EmptyContent = styled.div`
  ${Typography.body_sm_medium};
  color: var(--text-secondary);
  ${AlignCenter}
`;

const ActivityCard = styled.div<Props>`
  display: flex;
  padding: var(--space-16);
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ isProjectActivityCard }) => (isProjectActivityCard ? ' var(--space-12)' : 'var(--space-18)')};
  border-radius: var(--border-radius-12);
  border: 0.5px solid var(--border-primary);
  background: var(--background-primary);
  box-shadow: var(--shadow-card);
  position: relative;
`;

const HeadSection = styled.div`
  ${FlexBetween}
  width: 100%;
  .see-all-link {
    ${Typography.body_sm_medium};
    color: var(--text-primary);
    background: var(--background-primary);
    border: none;
    cursor: pointer;
    ${AlignCenter}
    gap: var(--space-4);
    text-decoration: none;
    &:hover {
      color: var(--text-secondary);
      svg path {
        fill: var(--text-secondary);
      }
    }
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-18);
`;

const TitleText = styled.div`
  ${Typography.heading_sm};
  color: var(--text-primary);
`;

const ActivityTimeline = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-12);
  p {
    ${Typography.body_sm_medium};
    color: var(--text-secondary);
    margin: var(--space-0);
  }
`;

const ActivityContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-8);
`;

const ActivityItem = styled.div`
  ${AlignCenter}
  gap: var(--space-12);
`;

const ProfileDiv = styled.div`
  ${AlignCenter}
`;

const StatusIcon = styled.div`
  ${FlexCenter}
  border-radius: var(--border-radius-full);
  background-color: var(--neutral-secondary);
  margin-left: -5px;
  z-index: var(--z-index-base);
  svg {
    padding: var(--space-5);
  }
`;

const ActivityTextDiv = styled.div`
  ${AlignCenter}
  padding-block: var(--space-3);
  align-content: center;
  column-gap: var(--space-4);
  flex-wrap: wrap;
  p {
    ${Typography.body_md_medium};
    color: var(--text-primary);
    margin: var(--space-0);
  }
  span {
    ${Typography.body_md};
    color: var(--text-secondary);
    margin: var(--space-0);
  }
`;

export const HeaderBlock = styled.div`
  position: sticky;
  top: 0;
  ${AlignCenter}
  padding: var(--space-11) var(--space-24);
  gap: var(--space-6);
  border-bottom: 0.5px solid var(--border-primary);
  background: var(--background-secondary);
  z-index: var(--z-index-header);
  span {
    ${Typography.body_lg_medium};
    color: var(--text-secondary);
    padding: var(--space-2) var(--space-4);
  }
`;

const ActivityContainer = styled.div`
  display: flex;
  max-width: 700px;
  margin: var(--space-24) auto;
  padding-inline: var(--space-24);
  flex-direction: column;
  gap: var(--space-24);
  ${mobile_xl(css`
    margin-block: var(--space-8) var(--space-96);
    padding-inline: var(--space-20);
  `)}
`;

const ActivitySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
`;

const Head = styled.p`
  margin: var(--space-0);
  ${Typography.heading_md};
  color: var(--text-primary);
`;

const ButtonComponent = styled.div`
  ${FlexCenter}
`;
const Empty = styled.div<Props>`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  left: ${({ isProjectActivityCard }) => (isProjectActivityCard ? '50%' : 'calc(50% + 105px)')};
  ${mobile_xl(css`
    left: 50%;
  `)}
`;

export {
  Group,
  PodcastDiv,
  Top,
  Heading,
  Profiles,
  ProfileBorder,
  ProfileGroup,
  TaskDiv,
  Task,
  Icon,
  Content,
  Header,
  MilestoneBox,
  Milestone,
  Div,
  LeftDiv,
  RightDiv,
  SubDiv,
  AvatarImage,
  GraphBox,
  Dropdowns,
  LoadingImage,
  ResourcesSection,
  ResourceTitle,
  AddedLinkBox,
  LinkIcon,
  Text,
  ResourcesFileSection,
  WrapperDiv,
  CardMainDiv,
  Title,
  ContentDiv,
  TopSection,
  BottomSection,
  TextDiv,
  RowDiv,
  ChatContent,
  ProgressSection,
  StatusText,
  UserName,
  EmptyContent,
  ActivityContainer,
  HeadSection,
  ContentSection,
  TitleText,
  ActivityTimeline,
  ActivityContentDiv,
  ActivityItem,
  ProfileDiv,
  StatusIcon,
  ActivityTextDiv,
  Head,
  ActivityCard,
  LeftSection,
  ButtonComponent,
  ActivitySection,
  Empty,
  AddedLinkBoxLeft
};
